import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cx } from 'class-variance-authority';
import { type ReactNode, useState } from 'react';

export type TooltipProps = {
  children: ReactNode;
  disabled?: boolean;
  content?: string | ReactNode;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  align?: TooltipPrimitive.TooltipContentProps['align'];
  delayOpen?: number;
  fullWidth?: boolean;
  /** Min distance kept from the viewport edges so the tooltip never sits flush against the window. */
  collisionPadding?: TooltipPrimitive.TooltipContentProps['collisionPadding'];
};

const Tooltip = ({
  children,
  disabled,
  content,
  side = 'top',
  align = 'center',
  delayOpen = 0,
  fullWidth,
  collisionPadding = 16,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  // Radix still reports hovers while the tooltip is disabled, and a trigger that turns inert under the
  // pointer never reports leaving, so a held open state would pop the tooltip up once it's enabled.
  if (disabled && open) setOpen(false);

  return (
    <TooltipPrimitive.Provider delayDuration={delayOpen}>
      <div
        className={cx(
          'inline-flex justify-center items-center',
          { 'w-full': fullWidth },
          { 'w-fit h-fit': !fullWidth },
        )}
      >
        <TooltipPrimitive.Root open={!disabled && open} onOpenChange={setOpen}>
          <TooltipPrimitive.Trigger asChild>
            <div className={cx('inline-block', { 'h-fit w-fit': !fullWidth }, { 'w-full': fullWidth })}>{children}</div>
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              align={align}
              side={side}
              sideOffset={4}
              collisionPadding={collisionPadding}
              asChild
              forceMount
            >
              <div
                className={cx(
                  'max-w-[300px] px-2 py-1 text-wrap text-caption-3 text-quinary text-left rounded bg-tertiary shadow-md z-50 origin-[var(--radix-tooltip-content-transform-origin)]',
                  '[&[data-side=bottom]]:animate-slideDown',
                  '[&[data-side=top]]:animate-slideUp',
                  '[&[data-side=left]]:animate-slideLeft',
                  '[&[data-side=right]]:animate-slideRight',
                  'data-[state=delayed-open]:animate-scaleIn data-[state=delayed-closed]:animate-scaleOut',
                  'data-[state=instant-open]:animate-scaleIn data-[state=instant-closed]:animate-scaleOut',
                )}
              >
                {content}
              </div>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </div>
    </TooltipPrimitive.Provider>
  );
};

export default Tooltip;
